import React, { useState, useRef, useEffect } from 'react';
import { industrias, subindustriasPorIndustria } from '../data/profileData';
interface MultiSelectTagsProps {
  selectedValues: string[];
  onChange: (values: string[]) => void;
  error?: string;
  disabled?: boolean;
  options?: string[]; // Opciones dinámicas (si no se proporciona, usa industrias por defecto)
  placeholder?: string; // Placeholder personalizado
  groupedOptions?: Record<string, string[]>; // Opciones agrupadas por categoría (ej: por temática)
  /** Si false, no se muestra el buscador dentro del desplegable (p. ej. en filtros) */
  allowSearch?: boolean;
}
const MultiSelectTags: React.FC<MultiSelectTagsProps> = ({
  selectedValues,
  onChange,
  error,
  disabled = false,
  options,
  placeholder,
  groupedOptions,
  allowSearch = true
}) => {
  const availableOptions = options || industrias;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(availableOptions);
  const [filteredGroupedOptions, setFilteredGroupedOptions] = useState<Record<string, string[]>>({});
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  // Filtrar opciones basado en el término de búsqueda
  useEffect(() => {
    if (groupedOptions) {
      // Si hay opciones agrupadas, filtrar por grupo
      const filtered: Record<string, string[]> = {};
      Object.keys(groupedOptions).forEach(group => {
        const groupOptions = groupedOptions[group].filter(option =>
          !selectedValues.includes(option) &&
          (searchTerm.trim() === '' || option.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        if (groupOptions.length > 0) {
          filtered[group] = groupOptions;
        }
      });
      setFilteredGroupedOptions(filtered);
    } else {
      // Comportamiento normal para opciones planas
      if (searchTerm.trim() === '') {
        setFilteredOptions(availableOptions.filter(option => !selectedValues.includes(option)));
      } else {
        const filtered = availableOptions.filter(option =>
          option.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !selectedValues.includes(option)
        );
        setFilteredOptions(filtered);
      }
    }
    setHighlightedIndex(-1);
  }, [searchTerm, selectedValues, availableOptions, groupedOptions]);
  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        setIsFocused(false);
        setIsExpanded(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  // Focus en el input de búsqueda cuando se abre el dropdown
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);
  // Detectar si es tablet (769px - 1024px) y mobile/tablet (<= 1024px)
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 769 && width <= 1024);
      setIsMobileOrTablet(width <= 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);
  // Efecto para manejar el tooltip en el DOM
  useEffect(() => {
    if (tooltipRef.current && tooltipRef.current.parentNode) {
      tooltipRef.current.parentNode.removeChild(tooltipRef.current);
      tooltipRef.current = null;
    }
    if (visibleTooltip !== null && tooltipPosition !== null) {
      const subindustrias = subindustriasPorIndustria[visibleTooltip as keyof typeof subindustriasPorIndustria]?.subindustrias || [];
      if (subindustrias.length > 0) {
        // Detectar si está en modo oscuro
        const isDarkMode = document.documentElement.classList.contains('dark-theme');
        const tooltipDiv = document.createElement('div');
        tooltipDiv.className = 'tooltip-content';
        tooltipDiv.style.cssText = `
          position: fixed !important;
          left: ${tooltipPosition.left}px !important;
          top: ${tooltipPosition.top}px !important;
          z-index: 999999 !important;
          background-color: ${isDarkMode ? '#0f172a' : '#ffffff'} !important;
          border: 2px solid ${isDarkMode ? '#CE2B15' : '#ef4444'} !important;
          border-radius: 8px !important;
          padding: 12px 16px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          min-width: 280px !important;
          max-width: 400px !important;
          font-family: Montserrat, sans-serif !important;
          pointer-events: none !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        `;
        const titleDiv = document.createElement('div');
        titleDiv.style.cssText = `font-weight: 700; color: ${isDarkMode ? '#ffffff' : '#1e293b'}; font-size: 13px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid ${isDarkMode ? '#334155' : '#e5e7eb'};`;
        titleDiv.textContent = 'Incluye:';
        const ul = document.createElement('ul');
        ul.style.cssText = 'list-style: none; padding: 0; margin: 0;';
        subindustrias.forEach((subindustria) => {
          const li = document.createElement('li');
          li.style.cssText = `font-size: 12px; color: ${isDarkMode ? '#cbd5e1' : '#64748b'}; line-height: 1.6; padding: 4px 0; padding-left: 16px; position: relative;`;
          const bullet = document.createElement('span');
          bullet.style.cssText = 'position: absolute; left: 0; color: #CE2B15; font-weight: bold;';
          bullet.textContent = '•';
          li.appendChild(bullet);
          li.appendChild(document.createTextNode(subindustria));
          ul.appendChild(li);
        });
        tooltipDiv.appendChild(titleDiv);
        tooltipDiv.appendChild(ul);
        if (document.body) {
          document.body.appendChild(tooltipDiv);
          tooltipRef.current = tooltipDiv;
        }
        return () => {
          if (tooltipRef.current && tooltipRef.current.parentNode) {
            tooltipRef.current.parentNode.removeChild(tooltipRef.current);
          }
          tooltipRef.current = null;
        };
      }
    }
  }, [visibleTooltip, tooltipPosition]);
  // Efecto para cerrar el tooltip de industrias al hacer clic fuera (solo en mobile/tablet)
  useEffect(() => {
    if (visibleTooltip !== null && window.innerWidth <= 1024) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(target) &&
          !target.classList.contains('modalidad-info-icon') &&
          !target.closest('.modalidad-info-icon')
        ) {
          setVisibleTooltip(null);
          setTooltipPosition(null);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [visibleTooltip]);
  const handleContainerClick = () => {
    if (disabled) return; // No hacer nada si está deshabilitado
    setIsOpen(true);
    setIsFocused(true);
    setIsExpanded(true);
  };
  const handleContainerFocus = () => {
    setIsFocused(true);
  };
  const handleContainerBlur = () => {
    if (!isOpen) {
      setIsFocused(false);
      setIsExpanded(false);
    }
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return; // No hacer nada si está deshabilitado
    setSearchTerm(e.target.value);
  };
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Calcular total de opciones disponibles
    const totalOptions = groupedOptions 
      ? Object.values(filteredGroupedOptions).reduce((sum, options) => sum + options.length, 0)
      : filteredOptions.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : totalOptions - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (disabled) return; // No hacer nada si está deshabilitado
        if (groupedOptions) {
          // Obtener la opción seleccionada de las opciones agrupadas
          const allGroupedOptions: string[] = [];
          Object.values(filteredGroupedOptions).forEach(options => {
            allGroupedOptions.push(...options);
          });
          if (highlightedIndex >= 0 && highlightedIndex < allGroupedOptions.length) {
            selectOption(allGroupedOptions[highlightedIndex]);
          }
        } else {
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            selectOption(filteredOptions[highlightedIndex]);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };
  const selectOption = (option: string) => {
    if (disabled) return; // No hacer nada si está deshabilitado
    if (!selectedValues.includes(option)) {
      onChange([...selectedValues, option]);
    }
    setSearchTerm('');
    setHighlightedIndex(-1);
  };
  const removeTag = (tagToRemove: string) => {
    if (disabled) return; // No hacer nada si está deshabilitado
    onChange(selectedValues.filter(tag => tag !== tagToRemove));
    if (!isExpanded) {
      setIsFocused(false);
    }
  };
  const handleOptionClick = (option: string) => {
    if (disabled) return; // No hacer nada si está deshabilitado
    selectOption(option);
  };
  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '100%' }} ref={containerRef}>
      {/* Contenedor de tags */}
      <div
        className={`multi-select-tags-container ${disabled ? 'multi-select-tags-disabled' : ''}`}
        style={{
          display: 'flex',
          flexWrap: isExpanded ? 'wrap' : 'nowrap',
          alignItems: isExpanded ? 'flex-start' : 'center',
          gap: '6px',
          height: isExpanded ? 'auto' : '38px',
          minHeight: '38px',
          width: '100%',
          maxWidth: '100%',
          padding: '8px 12px',
          border: error ? '1px solid #ef4444' : (isFocused ? '1px solid #CE2B15' : '1px solid #e5e7eb'),
          borderRadius: '6px',
          backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'border-color 0.2s ease',
          boxShadow: isFocused && !error ? '0 0 0 2px rgba(206, 43, 21, 0.1)' : 'none',
          transform: isFocused && !error ? 'translateY(-1px)' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
        onClick={handleContainerClick}
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
        tabIndex={disabled ? -1 : 0}
      >
        {/* Tags seleccionados */}
         {selectedValues.map((tag, index) => {
           const maxVisibleChips = isTablet ? 1 : 2;
           const shouldShowChip = isExpanded || index < maxVisibleChips;
           if (!shouldShowChip) return null;
           const displayText = isExpanded ? tag : (tag.length > 14 ? tag.substring(0, 14) + '...' : tag);
           return (
             <div
               key={`${tag}-${index}`}
               className="multi-select-tag"
               style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '2px',
                 padding: '2px 6px',
                 backgroundColor: '#f1f5f9',
                 border: '1px solid #d1d5db',
                 borderRadius: '3px',
                 fontSize: '11px',
                 fontFamily: 'Montserrat, sans-serif',
                 color: '#374151',
                 whiteSpace: 'nowrap',
                 flexShrink: 0,
                 minWidth: 'fit-content',
                 maxWidth: isExpanded ? 'none' : '120px'
               }}
               title={tag}
             >
               <span>{displayText}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: '10px',
                    lineHeight: 1,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '12px',
                    height: '12px'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
         {/* Indicador de chips ocultas cuando no está expandido */}
         {!isExpanded && selectedValues.length > (isTablet ? 1 : 2) && (
          <div
            className="multi-select-tag-more"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '2px 6px',
              backgroundColor: '#e5e7eb',
              border: '1px solid #d1d5db',
              borderRadius: '3px',
              fontSize: '11px',
              fontFamily: 'Montserrat, sans-serif',
              color: '#6b7280',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              cursor: disabled ? 'not-allowed' : 'pointer'
            }}
            onClick={(e) => {
              if (disabled) return; // No hacer nada si está deshabilitado
              e.stopPropagation();
              setIsExpanded(true);
              setIsOpen(true);
              setIsFocused(true);
            }}
          >
             +{selectedValues.length - (isTablet ? 1 : 2)} más
          </div>
        )}
        {/* Placeholder cuando no hay tags */}
        {selectedValues.length === 0 && (
          <span
            className="multi-select-tags-placeholder"
            style={{
              color: '#9ca3af',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              fontStyle: 'italic'
            }}
          >
            {placeholder || 'Seleccionar industrias...'}
          </span>
        )}
      </div>
      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          className="multi-select-tags-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            maxHeight: '250px',
            overflow: 'hidden'
          }}
        >
          {/* Buscador dentro del dropdown (opcional) */}
          {allowSearch && (
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder={placeholder ? placeholder.replace('Seleccionar', 'Buscar') : "Buscar industrias..."}
              className="multi-select-search-field"
              disabled={disabled}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                borderBottom: '1px solid #e5e7eb',
                fontSize: '13px',
                fontFamily: 'Montserrat, sans-serif',
                color: '#374151',
                outline: 'none',
                background: 'transparent',
                cursor: disabled ? 'not-allowed' : 'text'
              }}
            />
          )}
          {/* Lista de opciones */}
          <div style={{ maxHeight: allowSearch ? '180px' : '220px', overflow: 'auto' }}>
            {groupedOptions ? (
              // Renderizar opciones agrupadas
              Object.keys(filteredGroupedOptions).length > 0 ? (
                Object.keys(filteredGroupedOptions).map((groupName) => (
                  <div key={groupName} className="multi-select-group-container">
                    {/* Encabezado de grupo */}
                    <div
                      className="multi-select-group-header"
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb',
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10
                      }}
                    >
                      {groupName}
                    </div>
                    {/* Opciones del grupo */}
                    {filteredGroupedOptions[groupName].map((option, index) => {
                      const globalIndex = Object.keys(filteredGroupedOptions)
                        .slice(0, Object.keys(filteredGroupedOptions).indexOf(groupName))
                        .reduce((acc, key) => acc + filteredGroupedOptions[key].length, 0) + index;
                      const isDarkMode = document.documentElement.classList.contains('dark-theme');
                      return (
                        <div
                          key={option}
                          className="multi-select-option"
                          onClick={() => handleOptionClick(option)}
                          onMouseEnter={() => setHighlightedIndex(globalIndex)}
                          onMouseLeave={() => setHighlightedIndex(-1)}
                          style={{
                            padding: '8px 12px 8px 24px',
                            cursor: 'pointer',
                            backgroundColor: isDarkMode 
                              ? (globalIndex === highlightedIndex ? 'rgba(30, 41, 59, 0.5)' : 'transparent')
                              : (globalIndex === highlightedIndex ? '#f1f5f9' : 'transparent'),
                            color: isDarkMode ? undefined : '#374151',
                            fontFamily: 'Montserrat, sans-serif',
                            fontSize: '14px'
                          }}
                        >
                          <span>{option}</span>
                        </div>
                      );
                    })}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontStyle: 'italic',
                    fontSize: '13px',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                >
                  {searchTerm ? 'No se encontraron resultados' : 'No hay opciones disponibles'}
                </div>
              )
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const subindustrias = subindustriasPorIndustria[option as keyof typeof subindustriasPorIndustria]?.subindustrias || [];
                const hasSubindustrias = subindustrias.length > 0;
                return (
                  <div
                    key={option}
                    className="multi-select-option"
                    onClick={() => handleOptionClick(option)}
                    onMouseEnter={(e) => {
                      setHighlightedIndex(index);
                      if (hasSubindustrias && window.innerWidth > 1024) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const tooltipWidth = 300;
                        const spacing = 10;
                        let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                        let top = rect.bottom + spacing;
                        if (left + tooltipWidth > window.innerWidth) {
                          left = window.innerWidth - tooltipWidth - 10;
                        }
                        if (left < 10) {
                          left = 10;
                        }
                        const tooltipHeight = 200;
                        if (top + tooltipHeight > window.innerHeight) {
                          top = rect.top - tooltipHeight - spacing;
                        }
                        if (top < 10) {
                          top = 10;
                        }
                        setTooltipPosition({ left, top });
                        setVisibleTooltip(option);
                      }
                    }}
                    onMouseLeave={() => {
                      setVisibleTooltip(null);
                      setTooltipPosition(null);
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: index === highlightedIndex ? '#f1f5f9' : 'transparent',
                      color: '#374151',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>{option}</span>
                    {hasSubindustrias && isMobileOrTablet && (
                      <span
                        className="modalidad-info-icon"
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          border: '1px solid #d1d5db',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                          marginLeft: '8px'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (visibleTooltip === option) {
                            setVisibleTooltip(null);
                            setTooltipPosition(null);
                            return;
                          }
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tooltipWidth = 300;
                          const spacing = 10;
                          let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
                          let top = rect.bottom + spacing;
                          if (left + tooltipWidth > window.innerWidth) {
                            left = window.innerWidth - tooltipWidth - 10;
                          }
                          if (left < 10) {
                            left = 10;
                          }
                          const tooltipHeight = 200;
                          if (top + tooltipHeight > window.innerHeight) {
                            top = rect.top - tooltipHeight - spacing;
                          }
                          if (top < 10) {
                            top = 10;
                          }
                          setTooltipPosition({ left, top });
                          setVisibleTooltip(option);
                        }}
                      >
                        ?
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontStyle: 'italic',
                  fontSize: '13px',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                {searchTerm ? 'No se encontraron industrias' : 'Todas las industrias están seleccionadas'}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Mensaje de error */}
      {error && (
        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
          {error}
        </div>
      )}
    </div>
  );
};
export default MultiSelectTags;
